import { UrlModel } from '../model/UrlModel';
import _ from 'lodash';

/**
 * Service to manage the URL query parameters as a global resource
 */
export class UrlDataService {
	/**
	 * Initialize the service
	 * @param $location  The angular $location service
	 */
	constructor($location) {
		'ngInject';
		/**
		 * The angular $location service
		 * @type {$location}
		 */
		this.$location = $location;
		/**
		 * All of the registered event listeners
		 * @type {Array}
		 */
		this.listeners = [];
		/**
		 * A model of the Url Query params
		 * @type {UrlModel}
		 */
		this.model = new UrlModel();
		/**
		 * When true, do not fire listeners when hash changes
		 * @type {Boolean}
		 * @private
		 */
		this._skipNotification = false;

		// setup hashchange listener
		this.onHashChange = this.onHashChange.bind(this);
		angular.element(window).on('hashchange', this.onHashChange);
	}

	/**
	 * Handle the window.onhashchange event:
	 * Fire listeners to notify of changes
	 */
	onHashChange() {
		const [path, query] = this.$location.url().split('?');
		if (this.listeners.length > 0 && !this._skipNotification) {
			const newModel = new UrlModel();
			newModel.load(query);
			this.model.diff(newModel).forEach(diff => {
				this.notify(diff.param, diff.right, diff.left);
			});
		}
		this.model.load(query);
	}

	/**
	 * Get a single value from the URL
	 * @param {String} param  The name of the URL parameter
	 * @returns {String}
	 */
	get(param) {
		return this.model.get(param);
	}

	/**
	 * Get all parameters from the URL
	 * @returns {Object}
	 */
	getAll() {
		return this.model.values;
	}

	/**
	 * Set a single value on the URL
	 * @param {String} param  The name of the URL parameter
	 * @param {*} value  A value of any type to encode in the URL
	 * @returns {UrlDataService}
	 * @chainable
	 */
	set(param, value) {
		this.model.set(param, value);
		this.flush();
		return this;
	}

	/**
	 * Set a multiple values on the URL
	 * @param {*} kvPairs  pairs of param-values to set
	 * @returns {UrlDataService}
	 * @chainable
	 */
	setAll(kvPairs) {
		_.extend(this.model.values, kvPairs);
		this.flush();
		return this;
	}

	/**
	 * Register a callback to fire when the given URL param changes
	 * @param {String} param  The name of the param in the hash string
	 * @param {Function} callback  The handler for the change. Receives newVal, oldVal and param
	 * @param {$rootScope.Scope} [$scope]  If given, automatically remove this listener when the scope is destroyed
	 * @returns {UrlDataService}
	 * @chainable
	 */
	on(param, callback, $scope = null) {
		const listener = { param, callback };
		this.listeners.push(listener);
		if ($scope) {
			$scope.$on('$destroy', () => {
				const idx = this.listeners.indexOf(listener);
				if (idx > -1) {
					this.listeners.splice(idx, 1);
				}
			});
		}
		return this;
	}

	/**
	 * Deregister a callback
	 * @param {String} param  The name of the URL param
	 * @param {Function} callback  The handler that was registered
	 * @returns {UrlDataService}
	 * @chainable
	 */
	off(param, callback) {
		this.listeners = this.listeners.filter(listener => {
			return listener.param != param || listener.callback != callback;
		});
		return this;
	}

	/**
	 * Notify listeners that the given param was changed
	 * @param {String} param  The name of the param in the hash string
	 * @param {*} newVal  The new value
	 * @param {*} oldVal  The previous value
	 * @return {UrlDataService}
	 * @chainable
	 */
	notify(param, newVal, oldVal) {
		this.listeners.forEach(listener => {
			if (listener.param == param) {
				listener.callback(newVal, oldVal, param);
			}
		});
		return this;
	}

	/**
	 * Stringify current values and set window.location.hash using $location service
	 * @returns {UrlManager}
	 * @chainable
	 */
	flush() {
		const query = this.model.stringify();
		const [path] = this.$location.url().split('?');
		if (query === undefined) {
			this.$location.url(path);
		} else {
			this.$location.url(path + '?' + query);
		}
		return this;
	}

	/**
	 * Stringify current values, set window.location.hash and do not notify listeners
	 * @returns {UrlManager}
	 * @chainable
	 */
	flushWithoutNotifying() {
		this._skipNotification = true;
		this.flush();
		return this;
	}

	/**
	 * Bind a scope variable to a URL param of the same name such that if the variable changes
	 * the URL changes, and if the URL changes the variable is updated and the digest cycle is run
	 * @param {$rootScope.Scope} $scope  The scope object to which to bind the values
	 * @param {String|Object} name  The name of the value to watch
	 * @param {Function} [onChange]  A function to call after scope changes
	 *   Callback signature: onChange(newVal, oldVal, name)
	 * @returns {UrlDataService}
	 * @chainable
	 */
	bind($scope, name, onChange = null) {
		const doBind = () => {
			this.bindAll($scope, { [name]: $scope.vm[name] }, onChange);
		};
		if ($scope.vm) {
			doBind();
		} else {
			setTimeout(doBind, 0);
		}
		return this;
	}

	/**
	 * Bind multiple variables to scope with a URL parameter for each
	 * @param {$rootScope.Scope} $scope  The scope object to which to bind the values
	 * @param {Object} kvPairs  An object containing keys for the scope names and default values for the values
	 * @param {Function} [onChange] A callback to fire when URL or scope changes
	 *   Callback signature: onChange(newVals, oldVals) where newVals and oldVals are key-pair objects
	 * @returns {UrlDataService}
	 * @chainable
	 */
	bindAll($scope, kvPairs, onChange = null) {
		// count how many fields we are binding
		const pairCount = _.size(kvPairs);
		// we want to ignore initial digest firing, once for each pair
		let watchWait = pairCount;
		// define a function to bind
		const doBind = () => {
			// setup newVals/oldVals objects to pass to onChange callback
			let newVals = _.cloneDeep(kvPairs);
			let oldVals = {};
			// count the number of times $watch was fired
			let numWatches = 0;
			// when scope is destroyed, remove all params from URL
			$scope.$on('$destroy', () => {
				_.forOwn(kvPairs, (value, key) => {
					// setting to undefined removes it
					this.model.set(key, undefined);
				});
				this.flush();
			});
			// watch and listen for each URL param
			_.forOwn(kvPairs, (value, key) => {
				if (key in $scope.vm) {
					// populate oldVals with existing $scope value
					oldVals[key] = _.cloneDeep($scope.vm[key]);
				}
				// attach deep watch listener
				$scope.$watch(
					'vm.' + key,
					(newVal, oldVal) => {
						if (++numWatches <= watchWait) {
							return;
						}
						if (newVal === undefined) {
							newVal = _.cloneDeep(value);
						}
						// populate our newVals and oldVals
						newVals[key] = _.cloneDeep(newVal);
						oldVals[key] = _.cloneDeep(oldVal);
						// update the URL
						this.model.set(key, _.cloneDeep(newVal));
						flushAndCallback();
					},
					true
				);
				// attach listeners to the hashChange
				this.on(
					key,
					(newVal, oldVal) => {
						if (newVal === undefined) {
							// item was completely removed from URL: put back default
							newVal = _.cloneDeep(value);
							this.model.set(key, newVal);
							if (this.shouldNotifyOnRemove) {
								this.flush();
							} else {
								this.flushWithoutNotifying();
							}
						}
						// populate oldVals and newVals
						newVals[key] = _.cloneDeep(newVal);
						oldVals[key] = _.cloneDeep(oldVal);
						$scope.vm[key] = _.cloneDeep(newVal);
						// run digest
						if ($scope && !$scope.$$phase) {
							$scope.$apply();
						}
					},
					$scope
				);
				// pull values from URL to scope
				$scope.vm[key] = this.model.get(key);
				if ($scope.vm[key] === undefined) {
					// and fall back to default value
					$scope.vm[key] = _.cloneDeep(value);
				}
				// then set the URL values
				this.model.set(key, $scope.vm[key]);
			});
			// function to flush to address bar and fire onChange
			const flushAndCallback = () => {
				this.flush();
				onChange && onChange(newVals, oldVals);
				oldVals = _.cloneDeep(newVals);
			};
			// flush initially
			flushAndCallback();
		};
		if ($scope.vm) {
			// a component
			doBind();
		} else {
			// a controller
			setTimeout(doBind, 0);
		}
		return this;
	}

	/**
	 * Bind multiple variables to scope all under a single URL parameter
	 * @param {$rootScope.Scope} $scope  The scope object to which to bind the values
	 * @param {String} field  The name of the URL parameter
	 * @param {Object} kvPairs  An object containing keys for the scope names and default values for the values
	 * @param {Function} [onChange] A callback to fire when URL or scope changes
	 *   Callback signature: onChange(newVals, oldVals) where newVals and oldVals are key-pair objects
	 * @returns {UrlDataService}
	 * @chainable
	 */
	bindAllAs($scope, field, kvPairs, onChange = null) {
		// create a copy of the defaults
		kvPairs = _.cloneDeep(kvPairs);
		// count how many fields we are binding
		const pairCount = _.size(kvPairs);
		// we want to ignore initial digest firing, once for each pair
		let watchWait = pairCount;
		// define a function to bind
		const doBind = () => {
			// setup newVals/oldVals objects to pass to onChange callback
			let newVals = _.cloneDeep(kvPairs);
			let oldVals = {};
			// count the number of times $watch was fired
			let numWatches = 0;
			// when scope is destroyed, remove all params from URL
			$scope.$on('$destroy', () => {
				this.set(field, undefined);
			});
			// watch and listen for each URL param
			_.forOwn(kvPairs, (value, key) => {
				if (key in $scope.vm) {
					// populate oldVals with existing $scope value
					oldVals[key] = _.cloneDeep($scope.vm[key]);
				}
				// attach deep watch listener
				$scope.$watch(
					'vm.' + key,
					(newVal, oldVal) => {
						if (++numWatches <= watchWait) {
							return;
						}
						if (newVal === undefined) {
							newVal = _.cloneDeep(value);
						}
						// populate our newVals and oldVals
						newVals[key] = _.cloneDeep(newVal);
						if (!oldVals) {
							oldVals = {};
						}
						oldVals[key] = _.cloneDeep(oldVal);
						// update the URL
						_.set(this.model.values, `${field}.${key}`, newVal);
						flushAndCallback();
					},
					true
				);
			});
			// attach listeners to the hashChange
			this.on(
				field,
				(newVal, oldVal) => {
					if (newVal === undefined) {
						// item was completely removed from URL: put back default
						newVal = kvPairs;
						this.model.set(field, kvPairs);
						this.flush();
					}
					// populate oldVals and newVals
					newVals = _.cloneDeep(newVal);
					oldVals = _.cloneDeep(oldVal);
					_.extend($scope.vm, newVal);
					// run digest
					if ($scope && !$scope.$$phase) {
						$scope.$apply();
					}
				},
				$scope
			);
			// pull values from URL to scope
			_.forOwn(kvPairs, (value, key) => {
				$scope.vm[key] = _.get(this.model.values, `${field}.${key}`, value);
				_.set(this.model.values, `${field}.${key}`, $scope.vm[key]);
			});
			// function to flush to address bar and fire onChange
			const flushAndCallback = () => {
				this.flush();
				onChange && onChange(newVals, oldVals);
				oldVals = _.cloneDeep(newVals);
			};
			flushAndCallback();
		};
		if ($scope.vm) {
			// a component
			doBind();
		} else {
			// a controller
			setTimeout(doBind, 0);
		}
		return this;
	}
}
